import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo} from 'react';
import {Virtuoso, VirtuosoMockContext} from 'react-virtuoso';

import getConfig from 'getConfig';
import {
  usePublishingVersionDropdowns,
  UsePublishingVersionDropdowns,
} from 'notebook/version-history/hooks/usePublishingVersionDropdowns';
import RestrictiveRevertCard from 'notebook/version-history/RestrictiveRevertCard';
import {getLastDataSubmissionVersion} from 'notebook/version-history/restrictiveRevertUtil';
import VersionHistoryPanelRow from 'notebook/version-history/VersionHistoryPanelRow';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import notifications from 'notifications';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface VersionHistoryPanelProps {
  versions: Array<Version>;

  openVersionId?: number;
  navigateAndSetOpenVersionId: (versionId: number) => void;

  enableResetToVersion: boolean;
  resetToVersion: (version: Version) => void;
  enableDuplicateFromVersion: boolean;
  duplicateFromVersion: (version: Version) => void;
  unsaved?: boolean;

  versionParent?: {name?: string};
  navigateToVersionParent?: () => void;

  headerTitle?: string;
}

export type VersionHistoryPanelContext = Pick<
  VersionHistoryPanelProps,
  | 'openVersionId'
  | 'navigateAndSetOpenVersionId'
  | 'resetToVersion'
  | 'enableDuplicateFromVersion'
  | 'duplicateFromVersion'
> &
  Pick<
    UsePublishingVersionDropdowns,
    | 'hasPublishingVersions'
    | 'isPublishingVersionExpanded'
    | 'shouldShowPublishingVersionToggle'
    | 'togglePublishingVersion'
  > & {
    shouldEnableResetToVersionForRow: (version: Version) => boolean;
  };

function VersionHistoryPanel({
  versions,
  openVersionId,
  navigateAndSetOpenVersionId,
  enableResetToVersion,
  resetToVersion,
  enableDuplicateFromVersion,
  duplicateFromVersion,
  unsaved,
  versionParent,
  navigateToVersionParent,
  headerTitle,
}: VersionHistoryPanelProps): JSX.Element {
  const {
    hasPublishingVersions,
    isPublishingVersionExpanded,
    togglePublishingVersion,
    shouldShowPublishingVersionToggle,
    isVersionShown,
    expandAll,
    isExpandAllDisabled,
    collapseAll,
    isCollapseAllDisabled,
  } = usePublishingVersionDropdowns(versions, openVersionId);
  const versionsToShow = useMemo(
    () => versions.filter((version) => isVersionShown(version.id)),
    [versions, isVersionShown]
  );
  const lastDataSubmissionVersion = useMemo(
    () => getLastDataSubmissionVersion(versionsToShow),
    [versionsToShow]
  );

  const shouldEnableResetToVersionForRow = useCallback(
    (version: Version): boolean => {
      // if not enableResetVersion for panel, resetToVersion is not enabled for any row
      if (!enableResetToVersion) {
        return false;
      }
      // otherwise disable resetToVersion if version is before the last data submission version
      if (getConfig('PREVENT_REVERT_PAST_DATA_SUBMIT_ACTION') && lastDataSubmissionVersion) {
        return version.id >= lastDataSubmissionVersion?.id;
      }
      return true;
    },
    [lastDataSubmissionVersion, enableResetToVersion]
  );

  const openVersion = useCallback(
    (versionId: number) => {
      if (unsaved) {
        notifications.warn('Switching versions is disabled while a save is in progress.', {
          id: 'VersionHistoryPanel.openVersion',
        });
        return;
      }
      navigateAndSetOpenVersionId(versionId);
    },
    [unsaved, navigateAndSetOpenVersionId]
  );

  if (versionsToShow.length === 0) {
    return <HorizontalThrobber />;
  }
  const versionIndex = versionsToShow.findIndex((version) => version.id === openVersionId);
  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'VersionHistoryPanel', element: 'root'})}
    >
      <div className={css(styles.headerContainer)}>
        <div className={css(styles.titleContainer)}>
          <div className={css(styles.title)}>{headerTitle || 'Version history'}</div>
          {versionParent && navigateToVersionParent && (
            <Tooltip placement={FloatingPlacement.TOP} text="Duplicated from">
              <a
                className={classNames(css(styles.duplicatedFrom), 'duplicated-from-link')}
                onClick={navigateToVersionParent}
                {...getTestProps({component: 'VersionHistoryPanel', element: 'duplicatedFromLink'})}
              >
                <span>
                  <BenchlingIcon icon="copy" />
                </span>
                <span>{versionParent.name}</span>
              </a>
            </Tooltip>
          )}
        </div>
        {hasPublishingVersions && (
          <div className={classNames(css(styles.expandCollapseBar), 'expand-collapse-bar')}>
            <Button
              size={ButtonSize.MINI}
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              onClick={expandAll}
              disabled={isExpandAllDisabled}
            >
              Expand all
            </Button>
            <Button
              size={ButtonSize.MINI}
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              onClick={collapseAll}
              disabled={isCollapseAllDisabled}
            >
              Collapse all
            </Button>
          </div>
        )}
      </div>
      <div className={css(styles.versionsList)}>
        <Virtuoso<Version, VersionHistoryPanelContext>
          className="scrollbar-override"
          data={versionsToShow}
          initialTopMostItemIndex={versionIndex === -1 ? 0 : versionIndex}
          context={{
            openVersionId,
            navigateAndSetOpenVersionId: openVersion,
            resetToVersion,
            enableDuplicateFromVersion,
            duplicateFromVersion,
            hasPublishingVersions,
            isPublishingVersionExpanded,
            shouldShowPublishingVersionToggle,
            togglePublishingVersion,
            shouldEnableResetToVersionForRow,
          }}
          // eslint-disable-next-line react/no-unstable-nested-components
          itemContent={(_, data, context) => {
            const shouldShowRestrictiveRevertCard =
              getConfig('PREVENT_REVERT_PAST_DATA_SUBMIT_ACTION') &&
              data.id === lastDataSubmissionVersion?.id;

            return (
              <div
                className="version-history-panel-row-wrapper"
                {...getTestProps({component: 'VersionHistoryPanel', element: 'rowWrapper'})}
              >
                <VersionHistoryPanelRow rowData={data} context={context} />
                {shouldShowRestrictiveRevertCard && <RestrictiveRevertCard />}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '0.5em',
  },
  headerContainer: {
    margin: '1em',
    marginBottom: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  title: {
    fontSize: '16px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Color.GRAY_900,
  },
  duplicatedFrom: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '4px',
    gap: '0.2em',
    fontSize: '13px',
    color: Color.BLUE_500,
  },
  expandCollapseBar: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '12px',
    gap: '0.5em',
  },
  versionsList: {
    flex: 1,
  },
});

/**
 * When running VersionHistoryPanel in unit tests, we need to have the mock virtualization context
 * otherwise it will not render any rows.
 * see: https://virtuoso.dev/mocking-in-tests
 */
function VersionHistoryPanelInTest(props: VersionHistoryPanelProps): JSX.Element {
  const context = useMemo(() => ({viewportHeight: 800, itemHeight: 100}), []);
  return (
    <VirtuosoMockContext.Provider value={context}>
      <VersionHistoryPanel {...props} />
    </VirtuosoMockContext.Provider>
  );
}

/**
 * Allow reassigning VersionHistoryPanel in tests to VersionHistoryPanelInTest by using mockVirtualization().
 * Must cleanup with restore() afterwards.
 */
let DefaultVersionHistoryPanel = VersionHistoryPanel; // eslint-disable-line import-x/no-mutable-exports

// eslint-disable-next-line no-restricted-exports
export {DefaultVersionHistoryPanel as default};

export function mockVirtualization(): void {
  DefaultVersionHistoryPanel = VersionHistoryPanelInTest;
}

export function restore(): void {
  DefaultVersionHistoryPanel = VersionHistoryPanel;
}
